import type { ComputedLensAdapter } from '../../../core/assistant/hooks/useLensPresets';
import { ViewRulesMutationCommand } from '../../../core/assistant/commands/ViewRulesMutationCommand';
import type { ViewRule } from '../../../core/assistant/models/ViewRules';
import { useViewRulesStore } from '../../../core/assistant/stores/viewRulesStore';
import { getCommandManager } from '../../../hooks/useCommandManager';
import { SUCCESSION_RISK_COLORS } from './successionRiskPalette';

export { SUCCESSION_RISK_COLORS } from './successionRiskPalette';

export const SUCCESSION_LENS_ID = 'succession' as const;
export type SuccessionLensId = typeof SUCCESSION_LENS_ID;

export const SUCCESSION_LENS_RULE_NAMES = {
  risk: 'Succession Lens — Risk',
  keyPosition: 'Succession Lens — Key position',
} as const;

const isOwnedRule = (rule: ViewRule): boolean =>
  rule.name === SUCCESSION_LENS_RULE_NAMES.risk
  || rule.name === SUCCESSION_LENS_RULE_NAMES.keyPosition;

export function clearSuccessionLensRules(): void {
  const store = useViewRulesStore.getState();
  for (const rule of store.rules.filter(isOwnedRule)) {
    store.removeRule(rule.id);
  }
}

export function applySuccessionLensRules(): void {
  clearSuccessionLensRules();

  const store = useViewRulesStore.getState();
  const riskRuleId = store.addViewRule('colorBy', {
    field: 'successionRisk',
    name: SUCCESSION_LENS_RULE_NAMES.risk,
  });
  store.updateRuleDisplay(riskRuleId, {
    colorByField: {
      field: 'successionRisk',
      valueColors: { ...SUCCESSION_RISK_COLORS },
    },
    successorLens: { visibility: 'selection' },
  });

  const keyPositionRuleId = store.addViewRule('badge', {
    name: SUCCESSION_LENS_RULE_NAMES.keyPosition,
  });
  store.updateRule(keyPositionRuleId, {
    conditions: [{
      id: 'succession-lens-key-position',
      field: 'keyPosition',
      fieldType: 'boolean',
      operator: 'equals',
      value: true,
    }],
    display: {
      scopeMode: 'custom',
      badge: {
        icon: 'Star',
        color: '#f59e0b',
        position: 'top-right',
        tooltip: 'Key position',
      },
    },
  });
}

export function isSuccessionLensActive(rules: ViewRule[]): boolean {
  const riskRule = rules.find((rule) =>
    rule.enabled
    && rule.name === SUCCESSION_LENS_RULE_NAMES.risk
    && rule.display.colorByField?.field === 'successionRisk'
    && (rule.display.successorLens?.visibility === 'selection'
      || rule.display.successorLens?.visibility === 'all'));
  const keyPositionRule = rules.find((rule) =>
    rule.enabled
    && rule.name === SUCCESSION_LENS_RULE_NAMES.keyPosition
    && rule.display.scopeMode === 'custom'
    && rule.conditions.some((condition) =>
      condition.field === 'keyPosition'
      && condition.operator === 'equals'
      && condition.value === true));

  return Boolean(riskRule && keyPositionRule);
}

export const SUCCESSION_LENS_ADAPTER: ComputedLensAdapter<SuccessionLensId> = {
  apply: () => {
    void getCommandManager().execute(
      new ViewRulesMutationCommand('Apply Succession Lens', applySuccessionLensRules),
    );
  },
  clear: () => {
    void getCommandManager().execute(
      new ViewRulesMutationCommand('Clear Succession Lens', clearSuccessionLensRules),
    );
  },
  isActive: (_id, rules) => isSuccessionLensActive(rules),
};
